import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChartNoAxesCombined,
  BookOpen,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  MessageCircle,
  MoreHorizontal,
  Radar,
  ShieldCheck,
  SlidersHorizontal,
  TimerReset,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { LOGOUT } from '../constants/testIds/auth';
import { readApiJson } from '../lib/api';
import ActiveUsersBadge, { ACTIVE_USERS_IN_HEADER_QUERY } from './ActiveUsersBadge';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { PREPARATION_FAMILIES } from '../data/preparationProfiles';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { to: '/mock-test', label: 'Mock Test', Icon: TimerReset },
  { to: '/guides', label: 'Guides', Icon: BookOpen, menu: true },
  { to: '/scores', label: 'Scores', Icon: ChartNoAxesCombined },
  { to: '/settings', label: 'Settings', Icon: SlidersHorizontal }
];

const guideItems = [
  { to: '/raf-cbat-guide', label: 'CBAT Guide' },
  { to: '/raf-cbat-practice', label: 'Practice Test Library' },
  { to: '/guides#module-guides', label: 'Module Guides' },
  { to: '/methodology', label: 'How Scores Work' },
  { to: '/sources', label: 'Sources & Methodology' }
];

// Everything after the first message, both sides, oldest first. A server
// from before admin follow-ups sends only the account's own replies.
const messageThread = (message) => message.thread
  || (message.replies || []).map((reply) => ({ ...reply, from: 'user' }));

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // The dashboards' users-active badge sits beside the logo when there's room.
  const showsActiveUsers = useMediaQuery(ACTIVE_USERS_IN_HEADER_QUERY)
    && ['/dashboard', '/commercial/dashboard'].includes(location.pathname);
  const { user, profile, isAdmin, siteSettings, signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [inboxError, setInboxError] = useState('');
  const [selectedInboxMessageId, setSelectedInboxMessageId] = useState(null);
  const [inboxReply, setInboxReply] = useState('');
  const [replyingToInbox, setReplyingToInbox] = useState(false);
  const inboxRef = useRef(null);
  const guidesRef = useRef(null);
  const moreRef = useRef(null);

  const loadInbox = useCallback(async () => {
    try {
      // A message stays here, open for replies and follow-ups, until it's deleted.
      const data = await readApiJson('/account/messages?limit=50');
      const messages = data.messages || [];
      setInboxMessages(messages);
      setSelectedInboxMessageId((current) => (
        current && messages.some((message) => message.id === current) ? current : null
      ));
      setInboxError('');
    } catch (error) {
      setInboxError(error.message || 'Inbox unavailable.');
    }
  }, []);

  useEffect(() => {
    void loadInbox();
    const refreshTimer = window.setInterval(() => {
      void loadInbox();
    }, 60 * 1000);
    return () => window.clearInterval(refreshTimer);
  }, [loadInbox, user?.id]);

  useEffect(() => {
    if (!inboxOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!inboxRef.current?.contains(event.target)) setInboxOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [inboxOpen]);

  useEffect(() => {
    if (!guidesOpen && !moreOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (guidesOpen && !guidesRef.current?.contains(event.target)) setGuidesOpen(false);
      if (moreOpen && !moreRef.current?.contains(event.target)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [guidesOpen, moreOpen]);

  const unreadCount = inboxMessages.filter((message) => !message.is_read).length;
  const messageCount = inboxMessages.length;

  const displayName = profile?.display_name?.trim()
    || user?.user_metadata?.display_name?.trim()
    || user?.email
    || 'Account';

  const preparationPreferences = profile?.preparation_preferences || {};
  // Commercial Pilot Prep is still being built out -- hidden from regular
  // users (admins can still see/test it) until the site-wide toggle is on.
  const commercialTrackVisible = isAdmin || siteSettings?.commercial_track_enabled === true;
  const activeFamily = commercialTrackVisible
    ? (preparationPreferences.active_family || PREPARATION_FAMILIES.MILITARY)
    : PREPARATION_FAMILIES.MILITARY;
  const contextualNavItems = activeFamily === PREPARATION_FAMILIES.COMMERCIAL
    ? navItems.filter((item) => item.to !== '/mock-test').map((item) => (
      item.to === '/guides' ? { ...item, menu: false } : item
    ))
    : navItems;
  const items = isAdmin
    ? [...contextualNavItems, { to: '/admin', label: 'Admin', Icon: ShieldCheck }]
    : contextualNavItems;

  const handleSignOut = async () => {
    if (signingOut) return;
    setMobileMenuOpen(false);
    setGuidesOpen(false);
    setMoreOpen(false);
    setSigningOut(true);
    await new Promise((resolve) => setTimeout(resolve, 550));
    await signOut();
    navigate('/auth', { replace: true });
  };

  const goTo = (to) => {
    if (signingOut) return;
    setMobileMenuOpen(false);
    setInboxOpen(false);
    setGuidesOpen(false);
    setMoreOpen(false);
    navigate(to);
  };

  const selectInboxMessage = async (message) => {
    setSelectedInboxMessageId(message.id);
    setInboxReply('');
    if (message.is_read) return;

    setInboxMessages((current) => current.map((item) => (
      item.id === message.id ? { ...item, is_read: true } : item
    )));
    try {
      const data = await readApiJson(`/account/messages/${message.id}/read`, { method: 'POST' });
      setInboxMessages((current) => current.map((item) => (
        item.id === message.id ? data.account_message : item
      )));
    } catch (error) {
      setInboxError(error.message || 'The message could not be marked as read.');
      void loadInbox();
    }
  };

  const submitInboxReply = async (event, message) => {
    event.preventDefault();
    const reply = inboxReply.trim();
    if (!message || reply.length < 2 || replyingToInbox) return;

    setReplyingToInbox(true);
    try {
      const data = await readApiJson(`/account/messages/${message.id}/reply`, {
        method: 'POST',
        body: JSON.stringify({ message: reply })
      });
      if (data?.account_message) {
        setInboxMessages((current) => current.map((item) => (
          item.id === message.id ? data.account_message : item
        )));
      }
      setInboxReply('');
      setInboxError('');
    } catch (error) {
      setInboxError(error.message || 'Your reply could not be sent.');
    } finally {
      setReplyingToInbox(false);
    }
  };

  const deleteInboxMessage = async (message) => {
    if (!window.confirm('Delete this message? It will be removed from your inbox.')) return;
    try {
      await readApiJson(`/account/messages/${message.id}`, { method: 'DELETE' });
      setInboxMessages((current) => current.filter((item) => item.id !== message.id));
      setSelectedInboxMessageId(null);
      setInboxReply('');
      setInboxError('');
    } catch (error) {
      setInboxError(error.message || 'The message could not be deleted.');
    }
  };

  const renderItem = ({ to, label, Icon, menu }) => {
    const active = menu
      ? location.pathname === '/guides'
        || location.pathname.startsWith('/raf-cbat')
        || ['/methodology', '/sources'].includes(location.pathname)
      : location.pathname === to || location.pathname.startsWith(`${to}/`);
    const adminItem = to === '/admin';
    const button = (
      <button
        type="button"
        onClick={() => {
          if (menu) {
            setGuidesOpen((open) => !open);
            setMoreOpen(false);
          } else {
            goTo(to);
          }
        }}
        disabled={signingOut}
        aria-current={active ? 'page' : undefined}
        aria-expanded={menu ? guidesOpen : undefined}
        aria-haspopup={menu ? 'menu' : undefined}
        data-testid={menu ? 'guides-navigation-toggle' : undefined}
        className={`group relative flex items-center gap-2 rounded-md border px-2.5 py-2 text-xs font-medium transition-colors ${
          active
            ? adminItem
              ? 'border-[#2c7db8] bg-[#0f4c7a] text-white shadow-[inset_0_1px_rgba(255,255,255,0.1)]'
              : 'border-[#49647f] bg-[#13253d] text-white shadow-[inset_0_1px_rgba(255,255,255,0.06)]'
            : adminItem
              ? 'border-[#1f5f91] bg-[#0b2946] text-[#8fdcff] hover:border-[#68d9ff] hover:bg-[#0f4c7a] hover:text-white'
              : 'border-transparent text-[#8fa5bd] hover:border-[#273c55] hover:bg-[#0d192d] hover:text-white'
        } ${signingOut ? 'cursor-not-allowed opacity-50' : ''}`}
      >
        <Icon size={14} strokeWidth={1.8} aria-hidden="true" />
        <span>{label}</span>
        {menu && <ChevronDown size={12} className={`transition-transform ${guidesOpen ? 'rotate-180' : ''}`} aria-hidden="true" />}
        {active && <span className="absolute -bottom-[9px] left-2 right-2 h-px bg-[#68d9ff] shadow-[0_0_8px_#68d9ff]" />}
      </button>
    );

    if (menu) {
      return (
        <div key={to} ref={guidesRef} className="relative">
          {button}
          {guidesOpen && (
            <div className="absolute left-0 top-12 z-[70] w-60 overflow-hidden rounded-md border border-[#38546f] bg-[#050914] p-1.5 shadow-[0_18px_45px_rgba(0,0,0,0.65)]" role="menu" data-testid="guides-navigation-menu">
              {guideItems.map((item) => (
                <button key={item.to} type="button" role="menuitem" onClick={() => goTo(item.to)} className="flex w-full items-center justify-between rounded px-3 py-2.5 text-left text-xs text-[#a8bad0] hover:bg-[#102238] hover:text-white">
                  {item.label}<span className="text-[#52677e]">→</span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <React.Fragment key={to}>{button}</React.Fragment>
    );
  };

  return (
    <>
      {signingOut && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#02050c]/95 px-4 backdrop-blur-sm">
          <div className="site-panel w-[420px] max-w-full overflow-hidden">
            <div className="site-panel__header">
              <span>Session close</span>
              <span className="flex items-center gap-2 text-[#67e0ae]"><span className="site-status-dot" /> Sync active</span>
            </div>
            <div className="space-y-5 px-7 py-8 text-center">
              <div className="relative mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-[#304860] bg-[#050b16]">
                <Radar className="text-[#68d9ff]" size={34} strokeWidth={1.3} />
                <div className="absolute inset-2 animate-spin rounded-full border border-transparent border-t-[#68d9ff]" />
              </div>
              <div>
                <div className="text-lg font-semibold text-white">Saving your session</div>
                <div className="mt-1 text-sm text-[#8fa5bd]">Returning to the sign-in screen…</div>
              </div>
              <div className="h-1 overflow-hidden rounded-full bg-[#050914]">
                <div className="h-full w-2/3 animate-pulse rounded-full bg-gradient-to-r from-[#426eff] to-[#68d9ff]" />
              </div>
            </div>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-50 border-b border-[#263a50]/80 bg-[#050914]/88 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1320px] items-center gap-4 px-4 sm:px-6">
          <button
            type="button"
            onClick={() => goTo('/dashboard')}
            disabled={signingOut}
            className="group flex shrink-0 items-center gap-3 text-left"
            aria-label="CBAT Academy dashboard"
          >
            <span className="relative flex h-9 w-9 items-center justify-center rounded-md border border-[#38546f] bg-[#0d192d] shadow-[inset_0_1px_rgba(255,255,255,0.07)]">
              <Radar size={20} strokeWidth={1.5} className="text-[#68d9ff] transition-transform group-hover:rotate-12" />
              <span className="absolute right-1 top-1 h-1 w-1 rounded-full bg-[#67e0ae] shadow-[0_0_6px_#67e0ae]" />
            </span>
            <span>
              <span className="block font-['IBM_Plex_Sans_Condensed'] text-[15px] font-semibold tracking-[0.04em] text-white">
                CBAT<span className="text-[#68d9ff]">//</span>ACADEMY
              </span>
              <span className="hidden font-mono text-[8px] uppercase tracking-[0.16em] text-[#60758d] sm:block">
                Cognitive training system
              </span>
            </span>
          </button>

          {showsActiveUsers && <ActiveUsersBadge compact />}

          <nav className="ml-auto hidden items-center gap-1 min-[1180px]:flex" aria-label="Primary navigation">
            {items.map((item) => renderItem(item))}
          </nav>

          <nav className="ml-auto hidden items-center gap-1 lg:flex min-[1180px]:hidden" aria-label="Primary navigation">
            {items.filter((item) => ['/dashboard', '/mock-test', '/scores'].includes(item.to)).map((item) => renderItem(item))}
            <div className="relative" ref={moreRef}>
              <button
                type="button"
                onClick={() => {
                  setMoreOpen((open) => !open);
                  setGuidesOpen(false);
                }}
                aria-expanded={moreOpen}
                aria-haspopup="menu"
                data-testid="more-navigation-toggle"
                className="group relative flex items-center gap-2 rounded-md border border-transparent px-2.5 py-2 text-xs font-medium text-[#8fa5bd] transition-colors hover:border-[#273c55] hover:bg-[#0d192d] hover:text-white"
              >
                <MoreHorizontal size={14} aria-hidden="true" /> More <ChevronDown size={12} className={`transition-transform ${moreOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {moreOpen && (
                <div className="absolute right-0 top-12 z-[70] w-60 overflow-hidden rounded-md border border-[#38546f] bg-[#050914] p-1.5 shadow-[0_18px_45px_rgba(0,0,0,0.65)]" role="menu" data-testid="more-navigation-menu">
                  <button type="button" role="menuitem" onClick={() => goTo('/guides')} className="flex w-full items-center gap-2 rounded px-3 py-2.5 text-left text-xs text-[#a8bad0] hover:bg-[#102238] hover:text-white"><BookOpen size={13} /> Guides</button>
                  <button type="button" role="menuitem" onClick={() => goTo('/settings')} className="flex w-full items-center gap-2 rounded px-3 py-2.5 text-left text-xs text-[#a8bad0] hover:bg-[#102238] hover:text-white"><SlidersHorizontal size={13} /> Settings</button>
                  {isAdmin && <button type="button" role="menuitem" onClick={() => goTo('/admin')} className="flex w-full items-center gap-2 rounded px-3 py-2.5 text-left text-xs text-[#8fdcff] hover:bg-[#0f4c7a] hover:text-white"><ShieldCheck size={13} /> Admin</button>}
                </div>
              )}
            </div>
          </nav>

          <a
            href="https://discord.gg/UdyGXP6ZCM"
            target="_blank"
            rel="noopener noreferrer"
            className={`hidden items-center gap-2 rounded-md border border-[#1f6f45] bg-[#0f5f38] px-3 py-2 text-xs font-semibold text-white shadow-[0_0_14px_rgba(103,224,174,0.12)] transition-colors hover:border-[#67e0ae] hover:bg-[#12864d] lg:flex ${
              signingOut ? 'pointer-events-none opacity-50' : ''
            }`}
            aria-label="Join the CBAT Academy Discord server"
            data-testid="discord-nav-link"
          >
            <MessageCircle size={14} strokeWidth={1.9} aria-hidden="true" />
            <span>Discord</span>
          </a>

          {messageCount > 0 && (
            <div ref={inboxRef} className="relative ml-auto lg:ml-0">
              <button
                type="button"
                className="site-icon-button relative h-9 w-9"
                onClick={() => {
                  setInboxOpen((open) => !open);
                  if (!inboxOpen) void loadInbox();
                }}
                aria-label={unreadCount > 0
                  ? `New account messages, ${unreadCount} unread`
                  : `${messageCount} account message${messageCount === 1 ? '' : 's'}`}
                aria-expanded={inboxOpen}
                data-testid="account-inbox-button"
              >
                <Mail size={16} />
                {unreadCount > 0 && (
                  <span
                    className="absolute -right-1 -top-1 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-[#ff5d6c] px-1 font-mono text-[8px] font-bold text-white"
                    data-testid="account-inbox-unread-count"
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

            {inboxOpen && (
              <section
                className="absolute right-0 top-11 z-[70] w-[min(360px,calc(100vw-2rem))] overflow-hidden rounded-md border border-[#38546f] bg-[#050914] text-left shadow-[0_18px_45px_rgba(0,0,0,0.65)]"
                aria-label="Account messages"
                data-testid="account-inbox-panel"
              >
                <div className="flex items-center justify-between border-b border-[#263a50] bg-[#0d192d] px-4 py-3">
                  <div>
                    <div className="text-xs font-semibold text-white">Account inbox</div>
                    <div className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.12em] text-[#71869d]">
                      {messageCount} message{messageCount === 1 ? '' : 's'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setInboxOpen(false)}
                    className="site-icon-button h-7 w-7"
                    aria-label="Close inbox"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="max-h-[420px] overflow-y-auto">
                  {inboxError && (
                    <div className="border-b border-[#5b3038] bg-[#241015] px-4 py-2 text-xs text-[#ff9ba4]" role="alert">
                      {inboxError}
                    </div>
                  )}
                  {inboxMessages.map((message) => {
                    return (
                      <React.Fragment key={message.id}>
                        <button
                          type="button"
                          onClick={() => void selectInboxMessage(message)}
                          className="block w-full border-b border-[#1b2d42] bg-[#102238] px-4 py-3 text-left transition-colors hover:bg-[#0d192d]"
                          aria-expanded={selectedInboxMessageId === message.id}
                          data-testid={`account-message-${message.id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <span className="text-xs font-semibold text-white">{message.subject}</span>
                            {!message.is_read && (
                              <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#68d9ff] shadow-[0_0_7px_#68d9ff]" />
                            )}
                          </div>
                          <div className="mt-1.5 whitespace-pre-wrap break-words text-[11px] leading-relaxed text-[#8fa5bd]">
                            {message.message}
                          </div>
                          <div className="mt-2 font-mono text-[8px] uppercase tracking-[0.08em] text-[#52677e]">
                            {message.created_at ? new Date(message.created_at).toLocaleString() : 'CBAT Academy'}
                          </div>
                        </button>
                        {selectedInboxMessageId === message.id && messageThread(message).length > 0 && (
                          <div className="space-y-2 border-b border-[#1b2d42] bg-[#081323] px-4 py-3" data-testid={`account-message-replies-${message.id}`}>
                            {messageThread(message).map((entry) => (
                              <div
                                key={entry.id || entry.created_at}
                                className={`rounded border px-3 py-2 ${entry.from === 'admin' ? 'border-[#263a50] bg-[#102238]' : 'border-[#24513f] bg-[#0b1f19]'}`}
                              >
                                <div className={`font-mono text-[8px] uppercase tracking-[0.08em] ${entry.from === 'admin' ? 'text-[#68d9ff]' : 'text-[#67e0ae]'}`}>
                                  {entry.from === 'admin' ? 'CBAT Academy' : 'You'} · {entry.created_at ? new Date(entry.created_at).toLocaleString() : ''}
                                </div>
                                <p className="mt-1 whitespace-pre-wrap break-words text-[11px] leading-relaxed text-[#dbe7f2]">{entry.message}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {selectedInboxMessageId === message.id && (
                          <form
                            className="space-y-2 border-b border-[#263a50] bg-[#081323] px-4 py-3"
                            onSubmit={(event) => void submitInboxReply(event, message)}
                            data-testid={`account-message-reply-form-${message.id}`}
                          >
                            <label className="block text-[10px] font-semibold uppercase tracking-[0.08em] text-[#8fa5bd]" htmlFor={`account-message-reply-${message.id}`}>
                              {messageThread(message).length > 0 ? 'Send another message' : 'Reply'}
                            </label>
                            <textarea
                              id={`account-message-reply-${message.id}`}
                              rows={3}
                              minLength={2}
                              maxLength={5000}
                              value={inboxReply}
                              onChange={(event) => setInboxReply(event.target.value)}
                              className="w-full resize-y rounded border border-[#38546f] bg-[#050914] p-2 text-xs leading-relaxed text-white outline-none placeholder:text-[#52677e] focus:border-[#68d9ff]"
                              placeholder="Write your reply"
                              data-testid={`account-message-reply-${message.id}`}
                            />
                            <div className="flex items-center justify-between gap-3">
                              <span className="font-mono text-[8px] text-[#52677e]">{inboxReply.length} / 5000</span>
                              <button
                                type="button"
                                onClick={() => void deleteInboxMessage(message)}
                                className="ml-auto rounded border border-[#6d3540] bg-[#1b0b10] px-3 py-1.5 text-[10px] font-semibold text-[#ffb3bb] hover:border-[#ff8792] hover:text-white"
                                data-testid={`account-message-delete-${message.id}`}
                              >
                                Delete
                              </button>
                              <button
                                type="submit"
                                disabled={inboxReply.trim().length < 2 || replyingToInbox || message.can_reply === false}
                                className="rounded border border-[#2a8a61] bg-[#0f5f38] px-3 py-1.5 text-[10px] font-semibold text-white hover:border-[#67e0ae] hover:bg-[#12864d] disabled:cursor-not-allowed disabled:opacity-50"
                                data-testid={`account-message-reply-send-${message.id}`}
                              >
                                {replyingToInbox ? 'Sending...' : 'Send reply'}
                              </button>
                            </div>
                          </form>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </section>
            )}
            </div>
          )}

          <div className="hidden items-center gap-2 border-l border-[#263a50] pl-4 lg:flex">
            <div className="max-w-[150px] text-right">
              <div className="truncate text-[11px] font-medium text-[#d6e3f0]">{displayName}</div>
              <div className="mt-0.5 flex items-center justify-end gap-1.5 font-mono text-[8px] uppercase tracking-[0.12em] text-[#67e0ae]">
                <span className="site-status-dot !h-1.5 !w-1.5" /> Authenticated
              </div>
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              data-testid={LOGOUT.button}
              disabled={signingOut}
              className="site-icon-button h-9 w-9 hover:!border-[#7c4049] hover:!text-[#ff7b87]"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={15} />
            </button>
          </div>

          <button
            type="button"
            className={`flex h-10 w-10 items-center justify-center rounded-md border border-[#2b425a] bg-[#0d192d] text-[#d6e3f0] transition-colors hover:border-[#68d9ff] hover:text-white lg:hidden ${
              signingOut ? 'cursor-not-allowed opacity-50' : ''
            }`}
            onClick={() => !signingOut && setMobileMenuOpen((open) => !open)}
            disabled={signingOut}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation-menu"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            data-testid="mobile-navigation-toggle"
          >
            {mobileMenuOpen ? <X size={19} aria-hidden="true" /> : <Menu size={19} aria-hidden="true" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div
            id="mobile-navigation-menu"
            data-testid="mobile-navigation-menu"
            className="border-t border-[#263a50]/80 bg-[#050914]/96 px-4 pb-4 pt-3 shadow-[0_18px_30px_rgba(0,0,0,0.35)] lg:hidden"
          >
            <nav className="grid gap-2" aria-label="Mobile navigation">
              {items.map(({ to, label, Icon }) => {
                const active = location.pathname === to || location.pathname.startsWith(`${to}/`);
                const adminItem = to === '/admin';
                return (
                  <button
                    key={to}
                    type="button"
                    onClick={() => goTo(to)}
                    disabled={signingOut}
                    aria-current={active ? 'page' : undefined}
                    className={`flex items-center gap-3 rounded-md border px-3 py-3 text-left text-sm font-medium transition-colors ${
                      active
                        ? adminItem
                          ? 'border-[#2c7db8] bg-[#0f4c7a] text-white'
                          : 'border-[#49647f] bg-[#13253d] text-white'
                        : adminItem
                          ? 'border-[#1f5f91] bg-[#0b2946] text-[#8fdcff] hover:border-[#68d9ff] hover:bg-[#0f4c7a] hover:text-white'
                          : 'border-[#20344a] bg-[#091325] text-[#a8bad0] hover:border-[#466482] hover:text-white'
                    } ${signingOut ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    <Icon size={16} strokeWidth={1.8} aria-hidden="true" />
                    <span>{label}</span>
                  </button>
                );
              })}
              <a
                href="https://discord.gg/UdyGXP6ZCM"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 rounded-md border border-[#1f6f45] bg-[#0f5f38] px-3 py-3 text-sm font-semibold text-white shadow-[0_0_14px_rgba(103,224,174,0.12)] transition-colors hover:border-[#67e0ae] hover:bg-[#12864d]"
                aria-label="Join the CBAT Academy Discord server"
                data-testid="mobile-discord-nav-link"
              >
                <MessageCircle size={16} strokeWidth={1.9} aria-hidden="true" />
                <span>Discord</span>
              </a>
              <button
                type="button"
                onClick={handleSignOut}
                data-testid="mobile-logout-button"
                disabled={signingOut}
                className="flex items-center gap-3 rounded-md border border-[#3a2630] bg-[#170b12] px-3 py-3 text-left text-sm font-medium text-[#ff9ba4] transition-colors hover:border-[#7c4049] hover:text-white"
              >
                <LogOut size={16} strokeWidth={1.8} aria-hidden="true" />
                <span>Sign out</span>
              </button>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
